import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { CalendarDays, WandSparkles } from "lucide-react";
import type { SeasonalEvent } from "~/integrations/firebase/collections/seasonal-events";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { useAnalytics } from "~/contexts/analytics-context";
import type { LeadTimeLabel } from "~/components/seasonal-events/calendar-utils";
import type { UpcomingSeasonalEvent } from "./utils";

const formatLeadTime = (leadTime: LeadTimeLabel, t: TFunction) => {
  switch (leadTime.unit) {
    case "days":
      return t("components:seasonalEvents.leadTime.days", {
        count: leadTime.count,
      });
    case "weeks":
      return t("components:seasonalEvents.leadTime.weeks", {
        count: leadTime.count,
      });
    case "today":
    default:
      return t("components:seasonalEvents.leadTime.today");
  }
};

export const SeasonalEventsDropdown = ({
  events,
  onEventSelect,
}: {
  events: UpcomingSeasonalEvent[];
  onEventSelect: (event: SeasonalEvent) => void;
}) => {
  const { t } = useTranslation(["books", "components"]);
  const { gaEvent } = useAnalytics();

  const pills = useMemo(
    () =>
      events.map(({ event, leadTime }) => ({
        event,
        leadTimeLabel: formatLeadTime(leadTime, t),
      })),
    [events, t],
  );

  const eventIds = events.map(({ event }) => event.event_id).join(",");
  const eventCount = events.length;

  useEffect(() => {
    gaEvent({
      type: "seasonal_events_shown",
      payload: {
        event_ids: eventIds,
        event_count: eventCount,
        surface: "books",
      },
    });
  }, [gaEvent, eventIds, eventCount]);

  const selectEvent = (event: SeasonalEvent) => {
    gaEvent({
      type: "seasonal_event_chosen",
      payload: { event_id: event.event_id, surface: "books" },
    });
    onEventSelect(event);
  };

  return (
    <div
      data-testid="seasonal-events-dropdown"
      className="absolute left-0 right-0 top-full z-40 mt-2 flex flex-col gap-y-2 rounded-xl border border-secondary-100 bg-popover py-[10px] shadow-lg"
    >
      <div className="flex items-center gap-1.5 px-3.5">
        <WandSparkles aria-hidden className="size-3.5 shrink-0 text-primary" />
        <Text as="span" variant="captionStrong">
          {t("books:search.seasonalEvents.header")}
        </Text>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-1">
        {pills.map(({ event, leadTimeLabel }) => (
          <Button
            key={event.event_id}
            type="button"
            variant="tertiary"
            size="small"
            className="gap-x-1.5 rounded-full px-3 py-1.5 shadow-none hover:bg-secondary-50"
            onClick={() => selectEvent(event)}
          >
            <CalendarDays
              aria-hidden
              className="size-3.5 shrink-0 text-secondary-500"
            />
            <Text as="span" variant="detail">
              {event.display_name}
            </Text>
            <Text as="span" variant="muted">
              · {leadTimeLabel}
            </Text>
          </Button>
        ))}
      </div>
    </div>
  );
};
